/**
 * Authentication Service
 * 
 * Comprehensive service layer for authentication operations
 * Handles login, logout, token management, and session validation
 * 
 * Features:
 * - JWT token management
 * - Automatic token refresh
 * - Session persistence
 * - Error handling
 * - Integration with backend PostgreSQL auth endpoints
 */

import { apiRequest } from '../api-client';
import { API_ENDPOINTS } from '../api-config';
import { encryptPassword } from '../encryption';
import { logger } from '@/lib/logger';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Login request payload matching backend LoginRequest model
 */
export interface LoginRequest {
  email: string;          // EmailStr in backend
  password: string;       // Plain text password
  tenant_code?: string;   // Organization code (e.g., ACME). Optional when accessing via tenant subdomain — backend resolves tenant from Host header.
}

/**
 * Backend login response structure
 * Note: access_token is included for WebSocket authentication (cross-origin)
 * HttpOnly cookies are also set by backend for API requests
 */
export interface LoginResponse {
  success: boolean;       // Request success status
  message: string;        // Success message
  session_id: string;     // Session ID for tracking
  expires_in: number;     // Token expiry in seconds (3600)
  access_token: string;   // JWT token for WebSocket auth (cross-origin doesn't get cookies)
  csrf_token?: string;    // CSRF token for state-changing requests (POST, PUT, PATCH, DELETE)
  user: BackendUser;      // User object from backend
}

/**
 * User model matching backend response structure
 */
export type CreateKind = 'agent' | 'knowledge_base' | 'craft_app' | 'workflow' | 'schedule' | 'evaluation';

export interface BackendUser {
  user_id: string;        // UUID
  email: string;          // Email address
  username: string;       // Username
  tenant_id: string;      // Tenant ID
  display_name?: string;  // Optional display name
  permissions?: Record<string, any>; // User permissions
  roles?: string[];       // User roles
  must_change_password?: boolean; // forced password change on first login
  /** Kinds this user may CREATE (tenant admins manage but do not create content). */
  create_capabilities?: Partial<Record<CreateKind, boolean>> | null;
  /** Feature switches the UI hides routes on (DATA-01: kb_classic = the classic KB engine). */
  features?: Partial<Record<FeatureKey, boolean>> | null;
  /** True when the server found an admin role (tenant.admin / owner / platform.admin, directly or via a group).
   *  Tenant-wide settings writes need it; permission-rich roles without it get read-only Settings. */
  admin_role?: boolean | null;
}

export type FeatureKey = 'kb_classic';

/**
 * Current user info response from /auth/me
 */
export interface CurrentUserResponse extends BackendUser {
  permissions: Record<string, any>; // All effective permissions
}

/**
 * Token refresh response
 */
export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  expires_in: number;
  access_token: string;
}

/**
 * Session validation result
 */
export interface SessionValidationResult {
  isValid: boolean;
  user?: BackendUser;
  error?: string;
}

// ============================================================================
// AUTHENTICATION SERVICE
// ============================================================================

export class AuthService {
  
  /**
   * Login user with email, password, and tenant_code
   *
   * @param credentials - Login credentials
   * @returns Promise<LoginResponse> - Login response with tokens and user
   */
  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      logger.debug('🔐 [AuthService] Attempting login for:', credentials.email);

      // Encrypt password before sending (AES-256-CBC with PBKDF2)
      const encryptedPassword = await encryptPassword(credentials.password);

      const response = await apiRequest<LoginResponse>({
        method: 'POST',
        url: API_ENDPOINTS.auth.login,
        data: {
          email: credentials.email,
          password: encryptedPassword, // Send encrypted password
          tenant_code: credentials.tenant_code
        }
      });

      // Validate response (no logging of tokens/sensitive data)

      // Validate response has expected structure
      if (!response) {
        logger.error('❌ [AuthService] Empty response received');
        throw new Error('Empty response from server');
      }

      if (!response.user) {
        logger.error('❌ [AuthService] Response missing user:', response);
        // See the note in auth.ts: a raw body field, guarded so a non-string
        // can never reach the user as "[object Object]".
        const raw = (response as any).detail ?? (response as any).message;
        const errorDetail = typeof raw === 'string' && raw ? raw : 'Invalid server response - missing user data';
        throw new Error(errorDetail);
      }

      logger.debug('✅ [AuthService] Login successful for user:', response.user.email);
      logger.debug('🍪 [AuthService] Tokens set as HttpOnly cookies by backend');
      logger.debug('🆔 [AuthService] Session ID:', response.session_id);

      // SECURITY: Tokens are now HttpOnly cookies set by backend
      // No token storage needed - cookies sent automatically

      return response;
    } catch (error: any) {
      logger.error('❌ [AuthService] Login failed:', error.message);
      throw new Error(error.message || 'Login failed. Please check your credentials.');
    }
  }

  /**
   * Logout current user
   * Calls backend logout endpoint which clears HttpOnly cookies
   */
  static async logout(): Promise<void> {
    try {
      logger.debug('🚪 [AuthService] Logging out user...');

      // Call backend logout endpoint (clears HttpOnly cookies and server-side session)
      await apiRequest({
        method: 'POST',
        url: API_ENDPOINTS.auth.logout
      });

      logger.debug('✅ [AuthService] Server logout successful - HttpOnly cookies cleared');
    } catch (error: any) {
      logger.warn('⚠️  [AuthService] Server logout failed:', error.message);
      throw error;
    }
  }

  /**
   * Get current authenticated user info
   * Calls /auth/me endpoint to get user with permissions
   */
  static async getCurrentUser(): Promise<CurrentUserResponse> {
    try {
      logger.debug('👤 [AuthService] Fetching current user info...');
      
      const response = await apiRequest<CurrentUserResponse>({
        method: 'GET',
        url: API_ENDPOINTS.auth.me
      });

      logger.debug('✅ [AuthService] Current user fetched:', response.email);
      return response;
    } catch (error: any) {
      logger.error('❌ [AuthService] Failed to fetch current user:', error.message);
      throw new Error('Failed to fetch user information');
    }
  }

  /**
   * Request a password reset email.
   *
   * Backend ALWAYS returns the same generic message regardless of whether
   * the account exists (anti-enumeration) — never surface a different
   * message for "unknown email" in the UI.
   *
   * @param email - Account email address
   * @param tenant_code - Optional organization code (omit when on a tenant subdomain)
   */
  static async forgotPassword(email: string, tenant_code?: string): Promise<{ message: string }> {
    try {
      logger.debug('📧 [AuthService] Requesting password reset for:', email);

      const response = await apiRequest<{ message: string }>({
        method: 'POST',
        url: API_ENDPOINTS.auth.forgotPassword,
        data: {
          email,
          ...(tenant_code ? { tenant_code } : {}),
        },
      });

      return response;
    } catch (error: any) {
      logger.error('❌ [AuthService] Forgot-password request failed:', error.message);
      throw new Error(error.message || 'Could not process the request. Please try again.');
    }
  }

  /**
   * Complete a password reset using a one-time token.
   *
   * On success, the backend invalidates all active sessions for the user —
   * a fresh login is always required afterward.
   *
   * @param token - Raw reset token from the emailed link
   * @param new_password - New plaintext password (backend hashes it)
   */
  static async resetPassword(token: string, new_password: string): Promise<{ success: boolean; message: string }> {
    try {
      logger.debug('🔑 [AuthService] Submitting password reset...');

      const response = await apiRequest<{ success: boolean; message: string }>({
        method: 'POST',
        url: API_ENDPOINTS.auth.resetPassword,
        data: { token, new_password },
      });

      return response;
    } catch (error: any) {
      logger.error('❌ [AuthService] Password reset failed:', error.message);
      throw error;
    }
  }

  /**
   * Refresh access token using HttpOnly cookies
   * Refresh token sent automatically via HttpOnly cookie
   */
  static async refreshToken(): Promise<RefreshTokenResponse> {
    try {
      logger.debug('🔄 [AuthService] Refreshing access token...');

      // Refresh token sent automatically via HttpOnly cookie
      const response = await apiRequest<RefreshTokenResponse>({
        method: 'POST',
        url: API_ENDPOINTS.auth.refresh
      });

      logger.debug('✅ [AuthService] Token refreshed successfully via HttpOnly cookies');
      return response;
    } catch (error: any) {
      logger.error('❌ [AuthService] Token refresh failed:', error.message);
      throw new Error('Session expired. Please login again.');
    }
  }

  /**
   * Validate current session via HttpOnly cookies
   * Makes API call to verify session
   */
  static async validateSession(): Promise<SessionValidationResult> {
    try {
      // Verify session by calling /auth/me (HttpOnly cookie sent automatically)
      const user = await this.getCurrentUser();
      return { isValid: true, user };
    } catch (error: any) {
      logger.warn('⚠️  [AuthService] Session validation failed:', error.message);
      return { isValid: false, error: error.message };
    }
  }

  /**
   * Check if user is currently authenticated
   * Makes API call to verify (HttpOnly cookies sent automatically)
   */
  static async isAuthenticated(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  // ============================================================================
  // LOCAL STORAGE (Non-sensitive data only)
  // ============================================================================

  /**
   * Get stored user info (non-sensitive data only)
   *
   * SECURITY: This only retrieves user metadata (email, name, tenant_id)
   * Tokens are NOT stored in localStorage - they are:
   * 1. Kept in memory (Zustand store) for the current session
   * 2. Managed via HttpOnly cookies by the backend
   */
  static async getStoredUser(): Promise<BackendUser | null> {
    if (typeof window === 'undefined') return null;

    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (!authStorage) return null;

      const parsed = JSON.parse(authStorage);
      return parsed.state?.user || null;
    } catch (error) {
      logger.error('❌ [AuthService] Failed to get stored user:', error);
      return null;
    }
  }

  /**
   * Clear stored non-sensitive auth data
   */
  static clearStoredData(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem('auth-storage');
      logger.debug('🧹 [AuthService] Local storage cleared');
    } catch (error) {
      logger.error('❌ [AuthService] Failed to clear storage:', error);
    }
  }
}

export default AuthService;